import { useEffect, useRef, useState } from "react";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";

const DELAY_MS = 2500;
const MAX_PROGRESS = 100;
const PROGRESS_STEP = 5;
const INTERVAL_MS = DELAY_MS / (MAX_PROGRESS / PROGRESS_STEP);
const ANIMATION_SETTLE_MS = 400;

interface DeferredRequestResult<T> {
  deferredValue: T;
  buildProgress: number | undefined;
  deferredEtag: string | undefined;
}

const useDeferredRequest = <T>(
  value: T,
  enabled: boolean,
  immediateKey?: string,
  shouldPause?: (appliedValue: T) => boolean
): DeferredRequestResult<T> => {
  const [deferredValue, setDeferredValue] = useState(value);
  const [buildProgress, setBuildProgress] = useState<number | undefined>();
  const [deferredEtag, setDeferredEtag] = useState<string | undefined>();
  const enabledRef = useRef(enabled);
  const previousImmediateKeyRef = useRef<string | undefined>(undefined);
  const requestPausedRef = useRef(false);
  const paused = shouldPause?.(deferredValue) === true;

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    const applyImmediately = immediateKey !== undefined && immediateKey !== previousImmediateKeyRef.current;
    previousImmediateKeyRef.current = immediateKey;

    if (paused) {
      requestPausedRef.current = true;
      return;
    }

    if (!enabledRef.current || applyImmediately || requestPausedRef.current) {
      requestPausedRef.current = false;
      setDeferredValue(value);
      return;
    }
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setDeferredEtag(generateGuid());

    let progress = 0;
    let settleTimeoutId: ReturnType<typeof setTimeout> | undefined;
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setBuildProgress(0);

    const intervalId = setInterval(() => {
      progress += PROGRESS_STEP;
      if (progress >= MAX_PROGRESS) {
        setBuildProgress(MAX_PROGRESS);
        clearInterval(intervalId);
        setDeferredValue(value);
        settleTimeoutId = setTimeout(() => setBuildProgress(undefined), ANIMATION_SETTLE_MS);
      } else {
        setBuildProgress(progress);
      }
    }, INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      clearTimeout(settleTimeoutId);
      setBuildProgress(undefined);
    };
  }, [immediateKey, paused, value]);

  return { deferredValue, buildProgress, deferredEtag };
};

export default useDeferredRequest;
